<template>
  <div :style="{'margin-bottom': window.isDesktop ? 0 : '15px'}">
    <div v-on:mouseover="highlightArea()" v-on:mouseleave="unhighlightArea()">
      <div style="display: inline" class="custom-control custom-checkbox" :id="'trackcheckbox' + area.id">
        <input type="checkbox" class="custom-control-input" :id="'acheckbox' + area.id" v-model="checked" />
        <label style="margin-right: 2px;" class="custom-control-label" :for="'acheckbox' + area.id">{{ area.name }}</label>
      </div>
      <span v-if="area === $store.state.editedArea">
        <img v-if="window.isDesktop" @click="setEdited(null)" style="height: 24px; cursor: pointer" :src="icons.unlock" />
      </span>
      <span v-else>
        <img v-if="window.isDesktop" @click="setEdited(area)" style="height: 24px; cursor: pointer" :src="icons.lock" />
      </span>
      &nbsp;
      <template v-if="areaSaving">
        <img style='height: 16px; animation: rotation 2s infinite linear;' :src="icons.spinnerBlack" />&nbsp;
      </template>
        <span v-if="($store.state.user) && (window.isDesktop)" va-b-tooltip.hover :title="$t('saveArea')">
          <img @click="saveArea" style="height: 24px; cursor: pointer;" :src="icons.save" />
        </span>
      <br>
    </div>
  </div>  
</template>

<script lang="ts">
import L, { LatLng } from 'leaflet';
import Area from '@/ts/Area';
import BaseComponent from './Base.vue';
import { getCenter } from 'geolib';
import axios, { type AxiosResponse } from 'axios';
import { AlertStatus } from '@/ts/types';
import * as icons from '@/ts/icons';
import { defineComponent, type PropType } from "vue";

export default defineComponent({
    extends: BaseComponent,
    data() {
        const plannedMarkers: any = undefined;
        const middleMarkers: any = undefined;
        const polygon: L.Polygon = undefined;
        const checked: boolean = undefined;

        return {
            checked,
            polygon,
            middleMarkers,
            plannedMarkers,
            areaSaving: false
        };
    },
    mounted() {
        this.showOrHideArea();
    },
    methods: {
        saveArea() {
            this.areaSaving = true;
            const obj = this.area.convertToApiSave();
            if (! this.area.onServer) {
              const name = prompt(this.$t('pleaseAreaName'));
              if (name) {
                obj.name = name;
                axios.post(window.apiHost + `api/areas/?full=true`, obj)
                .then((response: AxiosResponse) => {
                  this.area.id = response.data.id;
                  this.area.onServer = true;
                  this.area.name = name;
                  this.createAlert(AlertStatus.success, this.$t('areaSaved').toString(), 2000);
                }).catch((response: object) => {
                  this.createAlert(AlertStatus.danger, this.$t('areaSavedError').toString(), 2000);
                }).finally(() => {
                 this.areaSaving = false;
                });
              } else {
                this.areaSaving = false;
              } 
            } else {
              axios.put(window.apiHost + `api/areas/${this.area.id}/?full=true`, obj)
              .then((response: object) => {
                this.createAlert(AlertStatus.success, this.$t('areaSaved').toString(), 2000);
              }).catch((response: object) => {
                this.createAlert(AlertStatus.danger, this.$t('areaSavedError').toString(), 2000);
              }).finally(() => {
                this.areaSaving = false;
              });
            }
        },
        highlightArea() {
            
        },
        unhighlightArea() {
            
        },
        removeAllFromMap() {
            this.polygon.removeFrom(window.mainmap);
            for (const marker of this.plannedMarkers) {
              marker.removeFrom(window.mainmap);
            }
            for (const marker of this.middleMarkers) {
              marker.removeFrom(window.mainmap);
            }
        },
        showOrHideArea() {
            if (this.checked) { 
              this.polygon.addTo(window.mainmap);
              if (this.$store.state.editedArea === this.area) {
                for (const marker of this.plannedMarkers) {
                  marker.addTo(window.mainmap);
                }
                for (const marker of this.middleMarkers) {
                  marker.addTo(window.mainmap);
                }
              }
            } else {
              this.removeAllFromMap();
            }
        },
        dragEnd(event: L.LeafletEvent, point: L.LatLng) {
            this.area.movePoint(point, event.target.getLatLng())
        },
        click(event: L.LeafletMouseEvent, point: L.LatLng) {
            if (event.originalEvent.ctrlKey || event.originalEvent.metaKey || event.originalEvent.shiftKey) {
              this.area.removePoint(point);
            }
        },
        clickNew(event: L.LeafletMouseEvent, point: L.LatLng) {
            this.area.addPointMiddle(point, event.latlng);
            L.DomEvent.stopPropagation(event);
            return false;
        },
        createMapObjects() {
            this.polygon = new L.Polygon(this.area.points);
            this.middleMarkers = [];
            this.plannedMarkers = [];
            let lastPoint = null;
            const markerIcon = L.icon({
              iconUrl: icons.redCircle,
              iconSize: [13, 13],
              iconAnchor: [6, 6],
            });
            const middleMarkerIcon = L.icon({
              iconUrl: icons.blueCircle,
              iconSize: [9, 9],
              iconAnchor: [4, 4],
            });
            for (const point of this.area.points) {
              const marker = new L.Marker(point, {draggable: true, icon: markerIcon, zIndexOffset: 100000, contextmenu: false, contextmenuItems: []});
              marker.on('dragend', (e: L.LeafletEvent) => {this.dragEnd(e, point)});
              marker.on('click', (e: L.LeafletMouseEvent) => {this.click(e, point)});
              if (lastPoint) {
                const middle = getCenter([{latitude: point.lat, longitude: point.lng}, {latitude: lastPoint.lat, longitude: lastPoint.lng}]);
                if (middle) {
                  const middleMarker = new L.Marker([middle.latitude, middle.longitude], {icon: middleMarkerIcon, zIndexOffset: 50000, contextmenu: false, contextmenuItems: []});
                  middleMarker.on('click', (e: L.LeafletMouseEvent) => {this.clickNew(e, point)});
                  this.middleMarkers.push(middleMarker);
                  lastPoint = point;
                }
              } else {
                lastPoint = point;
              }
              this.plannedMarkers.push(marker);
            }
        },
        setEdited(area: Area) {
            this.$store.commit('setEditedArea', area);
        },
        onCheckedChanged() {
            this.showOrHideArea();
        },
        onAreaPointsChanged() {
            this.removeAllFromMap();
            this.createMapObjects();
            this.showOrHideArea();
        },
        onEditedAreaChanged() {
            this.removeAllFromMap();
            this.createMapObjects();
            this.showOrHideArea();
        }
    },
    props: {
        area: { required: true,
            type: Object as PropType<Area>
        }
    },
    watch: {
        "checked": [{
            handler: "onCheckedChanged"
        }],
        "area.points": [{
            handler: "onAreaPointsChanged"
        }],
        "$store.state.editedArea": [{
            handler: "onEditedAreaChanged"
        }]
    }
})

</script>